import React from 'react';
import {withStyles} from 'material-ui/styles/index';
import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Heading,
} from '@adobe/react-spectrum';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import styles from './styles';
import {getDateFormat} from '../../../common/utils';

class MetricsScatterGraphModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen || this.props.isOpen !== this.state.isOpen) {
      this.setState({isOpen: this.props.isOpen});
    }
  }

  onOpenChange = (isOpen) => {
    this.setState({isOpen});
    if (!isOpen) {
      const {onClose} = this.props;
      if (onClose) {
        onClose();
      }
    }
  };

  dateToTimestamp(dateStr) {
    const parts = dateStr.split('-');
    // yyyy-MM-dd
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])).getTime();
  }

  formatDateParts = (year, month, day) => {
    const {activeLanguage} = this.props;
    const dateFormat = getDateFormat(activeLanguage || 'en-GB');
    const separator = dateFormat.includes('.') ? '.' : '/';
    const tokens = dateFormat.split(/[/.]/);
    const values = {
      DD: day,
      MM: month,
      YYYY: year,
    };

    return tokens.map(token => values[token] || token).join(separator);
  };

  formatRawDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;

    return this.formatDateParts(parts[0], parts[1], parts[2]);
  };

  formatAxisDate = (timestamp) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';

    return this.formatDateParts(
      `${date.getFullYear()}`,
      `${date.getMonth() + 1}`.padStart(2, '0'),
      `${date.getDate()}`.padStart(2, '0')
    );
  };

  formatAxisValue = (value) => {
    if (value === null || value === undefined) {
      return '';
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return `${value}`;
    }

    if (Number.isInteger(numericValue)) {
      return `${numericValue}`;
    }

    return numericValue.toFixed(2).replace(/\.0+$|0+$/, '').replace(/\.$/, '');
  };

  buildScatterData(data, seriesName) {
    if (!data || data.length === 0) return [];
    return data.map(point => ({
      x: this.dateToTimestamp(point.date),
      y: point.value,
      date: point.date,
      formattedDate: this.formatRawDate(point.date),
      seriesName,
    }));
  }

  renderTooltipContent = ({active, payload}) => {
    if (!active || !payload || !payload.length) return null;
    const {classes} = this.props;
    const yEntries = payload.filter(entry => entry.dataKey === 'y');
    return (
      <div className={classes.tooltip}>
        <p className={classes.tooltipLabel}>{payload[0].payload.formattedDate}</p>
        {yEntries.map((entry, i) => (
          <p key={i} className={classes.tooltipEntry} style={{color: entry.fill}}>
            {entry.payload.seriesName}: {this.formatAxisValue(entry.payload.y)}
          </p>
        ))}
      </div>
    );
  };

  getUniqueTicks(datasets) {
    if (!datasets) return undefined;
    const timestamps = new Set();
    datasets.forEach(ds => {
      if (ds.data) {
        ds.data.forEach(point => {
          timestamps.add(this.dateToTimestamp(point.date));
        });
      }
    });
    return Array.from(timestamps).sort((a, b) => a - b);
  }

  render() {
    const {classes, title, datasets, yAxisUnit, translate} = this.props;
    const {isOpen} = this.state;
    const ticks = this.getUniqueTicks(datasets);

    return (
      <DialogContainer isDismissable={false} onDismiss={() => this.onOpenChange(false)}>
        {isOpen && (
          <Dialog>
            <Heading>{title}</Heading>
            <Content>
              <div className={classes.chartContainer}>
                <ResponsiveContainer width="100%" height={350}>
                  <ScatterChart margin={{top: 10, right: 30, left: 0, bottom: 5}}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="x" name="Date" type="number" scale="time" domain={['auto', 'auto']} ticks={ticks} tickFormatter={this.formatAxisDate} />
                    <YAxis dataKey="y" name="Value" unit={yAxisUnit || ''} tickFormatter={this.formatAxisValue} />
                    <Tooltip content={this.renderTooltipContent} />
                    <Legend />
                    {datasets && datasets.map(ds => (
                      <Scatter
                        key={ds.label}
                        name={ds.label}
                        data={this.buildScatterData(ds.data, ds.label)}
                        fill={ds.color}
                      />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </Content>
            <ButtonGroup>
              <Button variant="secondary" onPress={() => this.onOpenChange(false)}>{translate('general_close')}</Button>
            </ButtonGroup>
          </Dialog>
        )}
      </DialogContainer>
    );
  }
}

export default withStyles(styles)(MetricsScatterGraphModal);
